import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase/config';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { useFeedback } from '../contexts/FeedbackContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [customToken, setCustomToken] = useState('');
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [validCode, setValidCode] = useState(false);
  const [resetType, setResetType] = useState(''); // 'firebase' or 'custom'

  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  
  // Extract oobCode or token from URL when component mounts
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('oobCode');
    const token = queryParams.get('token');
    
    if (code) {
      // Legacy Firebase password reset
      setOobCode(code);
      setResetType('firebase');
      verifyFirebaseCode(code);
    } else if (token) {
      // Custom token password reset
      setCustomToken(token);
      setResetType('custom');
      verifyCustomToken(token);
    } else {
      showError('Invalid Link', 'Invalid password reset link. Please request a new one.');
    }
  }, [location]);
  
  // Verify Firebase oobCode is valid
  const verifyFirebaseCode = async (code) => {
    try {
      // Verify the password reset code
      const email = await verifyPasswordResetCode(auth, code);
      setEmail(email);
      setValidCode(true);
    } catch (error) {
      console.error('Error verifying Firebase reset code:', error);
      showError('Link Expired', 'This password reset link is invalid or has expired. Please request a new one.');
    }
  };

  // Verify custom token is valid
  const verifyCustomToken = async (token) => {
    try {
      console.log('🔍 Verifying custom password reset token:', token);
      
      // Use Firebase Function to verify custom token
      const functions = getFunctions();
      const verifyTokenFunction = httpsCallable(functions, 'verifyPasswordResetToken');
      const result = await verifyTokenFunction({ token });
      
      if (result.data.success) {
        setEmail(result.data.email);
        setValidCode(true);

      } else {
        throw new Error(result.data.message || 'Token verification failed');
      }
    } catch (error) {
      console.error('❌ Error verifying custom reset token:', error);
      showError('Link Expired', 'This password reset link is invalid or has expired. Please request a new one.');
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (password !== confirmPassword) {
      showError('Validation Error', 'Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      showError('Validation Error', 'Password must be at least 6 characters');
      return;
    }
    
    const operation = async () => {
      if (resetType === 'firebase') {
        // Legacy Firebase password reset
        console.log('🔐 Resetting password with Firebase oobCode');
        await confirmPasswordReset(auth, oobCode, password);
      } else if (resetType === 'custom') {
        // Custom token password reset
        console.log('🔐 Resetting password with custom token');
        const functions = getFunctions();
        const resetPasswordFunction = httpsCallable(functions, 'resetPasswordWithToken');
        const result = await resetPasswordFunction({ 
          token: customToken, 
          newPassword: password 
        });
        
        if (!result.data.success) {
          throw new Error(result.data.message || 'Password reset failed');
        }
        

      } else {
        throw new Error('Invalid reset type');
      }
      
      // Show success message
      setSuccess(true);
      showSuccess('Password Reset Successful', 'Your password has been reset. Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      return { success: true };
    };

    try {
      await handleAsyncOperation(
        operation,
        {
          title: 'Resetting Password...',
          message: 'Please wait while we update your password.'
        },
        {
          title: 'Password Reset!',
          message: 'Your password has been reset successfully. Redirecting to login...',
          autoCloseDelay: 2000
        },
        {
          title: 'Reset Failed',
          message: 'Failed to reset password. Please try again.'
        }
      );
    } catch (error) {
      console.error('Error resetting password:', error);
      showError('Reset failed.', 'Please try again or request a new reset link.', 5000);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col">
      <Header />
      
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Reset your password
            </h2>
            {email && (
              <p className="mt-2 text-sm text-gray-600">
                For account: <span className="font-medium text-primary">{email}</span>
              </p>
            )}
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">

            
            
            {success ? (
              <div className="text-center py-6 space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-3">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Password reset successful!</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Your password has been reset successfully. You will be redirected to the login page.
                  </p>
                </div>
              </div>
            ) : validCode ? (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150"
                    placeholder="Enter your new password"
                  />
                  <p className="mt-1 text-xs text-gray-500">Must be at least 6 characters</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150"
                    placeholder="Confirm your new password"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  >
                    Reset Password
                  </button>
                </div>
              </form>
            ) : null}
            
            {!success && (
              <div className="pt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-500">
                      Remember your password?
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <a
                    href="/login"
                    className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Login
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
